import * as React from 'react'

import { styled } from '~/styled'

import { Tabs } from '../tabs/Tabs'
import type { SegmentedControlTheme } from './SegmentedControl.types'
import { SegmentedControlContext } from './SegmentedControlContext'

const StyledSegmentedControlRoot = styled(
  Tabs,
  {
    base: ['[&>div]:border-none'],
    variants: {
      size: {
        sm: ['w-[unset]!'],
        md: [],
        lg: []
      }
    }
  },
  { enabledResponsiveVariants: true }
)

export interface SegmentedControlRootProps extends React.ComponentProps<
  typeof StyledSegmentedControlRoot
> {
  theme?: SegmentedControlTheme
}

export const SegmentedControlRoot = ({
  size,
  theme = 'primary',
  defaultValue,
  children,
  ...props
}: React.PropsWithChildren<SegmentedControlRootProps>): React.JSX.Element => {
  const isControlled = props.value !== undefined

  const [internalValue, setInternalValue] = React.useState(defaultValue)

  const currentValue = isControlled ? props.value : internalValue
  const handleValueChange = isControlled
    ? props.onValueChange
    : setInternalValue

  React.useEffect(() => {
    if (!isControlled && defaultValue) {
      setInternalValue(defaultValue)
    }
  }, [defaultValue, isControlled])

  const value = React.useMemo(
    () => ({
      size,
      theme,
      defaultValue,
      onValueChange: handleValueChange,
      value: currentValue
    }),
    [size, theme, defaultValue, handleValueChange, currentValue]
  )

  return (
    <SegmentedControlContext.Provider value={value}>
      <StyledSegmentedControlRoot
        size={size}
        value={currentValue}
        onValueChange={handleValueChange}
        {...props}
      >
        {children}
      </StyledSegmentedControlRoot>
    </SegmentedControlContext.Provider>
  )
}
